/**
 * Блок «Структура оптовой торговли» (US-30 FR-4): восемь товарных групп
 * по кодам ОКЭД за отчётный год — таблица и круговая диаграмма того же
 * состава.
 *
 * Перечень групп исчерпывающий: сумма восьми групп равна итогу источника,
 * строки «прочее» на экране нет (FR-4.3). Итог берётся из строки «Всего»
 * выпуска, а не складывается из групп (FR-4.4): часть значений может быть
 * закрыта как конфиденциальная, и сумма строк тогда итогу не равна.
 *
 * Отчётный период блока — год, и он отстаёт от накопленного периода
 * плашки (FR-4.5/FR-6.3). Это нормальное состояние источника, а не
 * рассогласование данных, поэтому подпись периода у блока своя.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart } from 'recharts';

import {
  PeriodCaption,
  SourceNote,
  StatNoData,
  StatNumber,
  fmt,
  fmt2,
} from './statValue';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../lib/cn';
import type {
  TradeWholesaleGroup,
  TradeWholesaleResponse,
} from '../../../types/analytics';

/** Цвета сегментов — токены темы (chart-*, brand-*, status-info). */
const GROUP_COLORS = [
  '#4A63E7', // chart-indigo
  '#A3CE63', // chart-green
  '#6D7FE3', // chart-blue
  '#F2D9A4', // chart-sand
  '#34C24A', // brand-500
  '#2C6FD6', // status-info
  '#2C9C42', // brand-600
  '#C9ECD1', // brand-100
];

interface TradeWholesaleProps {
  data: TradeWholesaleResponse | null;
  loading: boolean;
}

export const TradeWholesale = ({ data, loading }: TradeWholesaleProps) => {
  const { t } = useTranslation();
  const [drillGroup, setDrillGroup] = useState<TradeWholesaleGroup | null>(
    null,
  );

  const groups = data?.groups ?? [];
  // Единица блока объявлена ответом у величин — экран её не подставляет.
  const unit =
    data?.total?.unit ??
    groups.find((group) => group.turnover?.unit != null)?.turnover?.unit ??
    null;

  // В диаграмму попадают только группы с известным оборотом: состояния
  // «конфиденциально» и «нет в выпуске» сегментом нулевой площади
  // изображать нельзя.
  const segments = groups.flatMap((group, index) => {
    const value = group.turnover;
    if (value === null || value.state !== 'value' || value.value === null) {
      return [];
    }
    return [
      {
        key: group.code ?? group.name ?? String(index),
        name: group.name ?? '—',
        value: value.value,
        color: GROUP_COLORS[index % GROUP_COLORS.length],
      },
    ];
  });

  const chartConfig: ChartConfig = Object.fromEntries(
    segments.map((segment) => [
      segment.key,
      { label: segment.name, color: segment.color },
    ]),
  );

  return (
    <Card className="shadow-card" data-testid="analytics-trade-wholesale">
      <CardHeader className="flex-row flex-wrap items-baseline justify-between gap-x-4 gap-y-1 space-y-0">
        <CardTitle>{t('analytics.trade.wholesale.title')}</CardTitle>
        {/* Годовой период блока — свой, он отстаёт от плашки (FR-4.5). */}
        <PeriodCaption
          period={data?.period ?? null}
          suffix={unit}
          testId="trade-wholesale-period"
        />
      </CardHeader>
      <CardContent>
        {loading ? (
          <BlockLoader />
        ) : groups.length === 0 ? (
          <StatNoData text={t('common.noData')} />
        ) : (
          <>
            <div className="flex flex-col gap-6 xl:flex-row xl:items-center">
              <div className="min-w-0 flex-1">
                <Table>
                  <TableHeader>
                    <TableRow className="border-surface-border hover:bg-transparent">
                      <TableHead className="h-auto py-2 text-text-faint">
                        {t('analytics.trade.wholesale.columnGroup')}
                      </TableHead>
                      <TableHead className="h-auto whitespace-nowrap py-2 text-right text-text-faint">
                        {t('analytics.trade.wholesale.columnTurnover')}
                      </TableHead>
                      <TableHead className="h-auto whitespace-nowrap py-2 text-right text-text-faint">
                        {t('analytics.trade.wholesale.columnShare')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Итог — из строки «Всего» источника (FR-4.4). */}
                    <TableRow
                      className="border-surface-border hover:bg-transparent"
                      data-testid="trade-wholesale-total"
                    >
                      <TableCell className="py-3 font-extrabold">
                        {t('analytics.trade.wholesale.totalRow')}
                      </TableCell>
                      <TableCell className="py-3 text-right">
                        <StatNumber value={data?.total ?? null} format={fmt} />
                      </TableCell>
                      <TableCell className="py-3 text-right font-extrabold tabular-nums">
                        {data?.total?.state === 'value' ? fmt2(100) : '—'}
                      </TableCell>
                    </TableRow>

                    {groups.map((group, index) => {
                      const drillable = group.details.length > 0;
                      const key = group.code ?? group.name ?? String(index);
                      return (
                        <TableRow
                          key={key}
                          data-testid={`trade-wholesale-group-${key}`}
                          className={cn(
                            'border-surface-border',
                            drillable
                              ? 'cursor-pointer hover:bg-brand-50/60'
                              : 'hover:bg-transparent',
                          )}
                          // Drill-through — только при наличии первичных
                          // данных группы (FR-10.1/FR-10.4).
                          tabIndex={drillable ? 0 : undefined}
                          role={drillable ? 'button' : undefined}
                          onClick={
                            drillable ? () => setDrillGroup(group) : undefined
                          }
                          onKeyDown={
                            drillable
                              ? (event) => {
                                  if (
                                    event.key === 'Enter' ||
                                    event.key === ' '
                                  ) {
                                    event.preventDefault();
                                    setDrillGroup(group);
                                  }
                                }
                              : undefined
                          }
                        >
                          <TableCell className="py-3">
                            <span className="flex items-baseline gap-2">
                              <span
                                className="size-2.5 shrink-0 self-center rounded-sm"
                                style={{
                                  backgroundColor:
                                    GROUP_COLORS[index % GROUP_COLORS.length],
                                }}
                                aria-hidden="true"
                              />
                              <span className="min-w-0 font-semibold leading-snug">
                                {group.name ?? '—'}
                                {group.code !== null && (
                                  <span className="ml-1.5 text-xs font-normal text-text-faint">
                                    {group.code}
                                  </span>
                                )}
                              </span>
                            </span>
                          </TableCell>
                          <TableCell className="py-3 text-right">
                            <StatNumber value={group.turnover} format={fmt} />
                          </TableCell>
                          <TableCell className="py-3 text-right">
                            {/* Доля — расчётная величина от итога (FR-4.6). */}
                            <StatNumber value={group.share} format={fmt2} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {segments.length > 0 && (
                <div className="w-full max-w-[220px] shrink-0 self-center">
                  <ChartContainer
                    config={chartConfig}
                    className="aspect-square"
                    data-testid="trade-wholesale-chart"
                  >
                    <PieChart>
                      <ChartTooltip
                        cursor={false}
                        content={<ChartTooltipContent hideLabel />}
                      />
                      <Pie
                        data={segments}
                        dataKey="value"
                        nameKey="name"
                        innerRadius="55%"
                        outerRadius="98%"
                        strokeWidth={2}
                      >
                        {segments.map((segment) => (
                          <Cell key={segment.key} fill={segment.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                </div>
              )}
            </div>

            <SourceNote source={data?.source ?? null} />
          </>
        )}
      </CardContent>

      {/* Первичные данные группы — коды товаров (FR-10.4); глубина
          раскрытия задаётся поставкой, экран её не выбирает. */}
      <Dialog
        open={drillGroup !== null}
        onOpenChange={(open) => {
          if (!open) setDrillGroup(null);
        }}
      >
        <DialogContent
          className="max-h-[85vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto"
          data-testid="trade-wholesale-drill"
        >
          {drillGroup !== null && (
            <>
              <DialogHeader>
                <DialogTitle className="text-left text-base leading-snug">
                  {drillGroup.name ?? '—'}
                </DialogTitle>
                <p className="text-sm text-text-muted">
                  {t('analytics.trade.wholesale.drillHint')}
                </p>
              </DialogHeader>
              <ul>
                {drillGroup.details.map((detail) => (
                  <li
                    key={detail.code ?? detail.name ?? ''}
                    className="flex items-baseline gap-4 border-b border-dashed border-surface-border py-2 text-sm last:border-b-0"
                  >
                    <span className="min-w-0 flex-1 leading-snug text-text-muted">
                      {detail.name ?? '—'}
                      {detail.code !== null && (
                        <span className="ml-1.5 text-xs text-text-faint">
                          {detail.code}
                        </span>
                      )}
                    </span>
                    <StatNumber
                      value={detail.turnover}
                      format={fmt}
                      className="shrink-0 text-right"
                    />
                  </li>
                ))}
              </ul>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};
